<script setup lang="ts">
  // Unified orders-history command bar (both /orders and /pos/history):
  // back (POS route only) + tabs (Todos / Mesas / Llevar / Delivery) + date,
  // status and price popovers | debounced search (page-side) | total badge.
  // On the Mesas tab: status + price swap for the tables popover, search
  // targets table or customer names and the badge counts sessions.
  import { computed, getCurrentInstance, ref } from 'vue';
  import { OrderStatus, OrderType } from '@/enums/OrderEnums';
  import {
    getPosOrderTypeIcon,
    getPosOrderTypeLabel,
  } from '@/domain/pos/order-type-ui';
  import type { IHistoryDateRange } from '@/domain/pos/history-date-presets';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosV2HistoryDatePopover from '@/components/pos-v2/history/PosV2HistoryDatePopover.vue';
  import PosV2HistoryStatusPopover from '@/components/pos-v2/history/PosV2HistoryStatusPopover.vue';
  import PosV2HistoryTablesPopover from '@/components/pos-v2/history/PosV2HistoryTablesPopover.vue';

  // 'tables' replaces the Seat tab: closed table sessions are the per-table
  // lens; individual seat orders remain under Todos.
  export type THistoryTypeTab =
    | 'all'
    | 'tables'
    | OrderType.Away
    | OrderType.Delivery;

  // priceMin/priceMax are optional-with-default: Vue 2 only skips the
  // runtime type check for null on NON-required props, and `number | null`
  // compiles to a required `Number` prop that warns on every null.
  const props = withDefaults(
    defineProps<{
      typeTab: THistoryTypeTab;
      search: string;
      dateRange: IHistoryDateRange;
      /** Applied status filter (empty = all). Hidden on the Mesas tab. */
      statuses: OrderStatus[];
      /** Applied table filter (empty = all). Mesas tab only. */
      tableIds: number[];
      /** Branch tables for the Mesas popover (hidden when empty). */
      tableOptions: { id: number; name: string }[];
      /** Applied price range (null = not narrowed). */
      priceMin?: number | null;
      priceMax?: number | null;
      total: number;
      /** Back button (POS route only; the back-office route has the menu). */
      showBack: boolean;
    }>(),
    { priceMin: null, priceMax: null }
  );

  const emit = defineEmits<{
    (e: 'update:typeTab', value: THistoryTypeTab): void;
    (e: 'update:search', value: string): void;
    (e: 'apply-date', value: IHistoryDateRange): void;
    (e: 'apply-statuses', value: OrderStatus[]): void;
    (e: 'apply-tables', value: number[]): void;
    (e: 'apply-price', value: { min: number | null; max: number | null }): void;
    (e: 'back'): void;
  }>();

  const restaurantStore = useRestaurantStore();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol ?? '$'
  );

  const tabs = computed(() => [
    {
      value: 'all' as const,
      label: i18nT('posV2.historyTypeAll'),
      icon: 'layers-outline',
    },
    {
      value: 'tables' as const,
      label: i18nT('posV2.historyTabTables'),
      icon: 'table-furniture',
    },
    ...[OrderType.Away, OrderType.Delivery].map((type) => ({
      value: type,
      label: getPosOrderTypeLabel(type),
      icon: getPosOrderTypeIcon(type),
    })),
  ]);

  const isTablesTab = computed(() => props.typeTab === 'tables');

  const searchPlaceholder = computed(() =>
    isTablesTab.value
      ? i18nT('posV2.historySearchTablesPlaceholder')
      : i18nT('posV2.historySearchPlaceholder')
  );

  const priceNarrowed = computed(
    () => props.priceMin != null || props.priceMax != null
  );

  const resultsLabelKey = computed(() => {
    if (isTablesTab.value) {
      return props.total === 1 ? 'posV2.sessionWord' : 'posV2.sessionsWord';
    }
    return props.total === 1 ? 'posV2.orderWord' : 'posV2.ordersWord';
  });

  //#region Price popover (pending inputs → applied on "Aplicar")

  const pendingMin = ref('');
  const pendingMax = ref('');

  function syncPendingFromApplied(): void {
    pendingMin.value = props.priceMin != null ? String(props.priceMin) : '';
    pendingMax.value = props.priceMax != null ? String(props.priceMax) : '';
  }

  function parsePriceInput(raw: string): number | null {
    let normalized = raw.trim();
    if (!normalized) return null;
    if (normalized.includes('.') && normalized.includes(',')) {
      // es-AR style "1.500,50": dots are thousand separators, comma decimal.
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(',', '.');
    }
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return parsed;
  }

  function applyPrice(close: () => void): void {
    let min = parsePriceInput(pendingMin.value);
    let max = parsePriceInput(pendingMax.value);

    // Swapped bounds: normalize instead of erroring.
    if (min != null && max != null && min > max) [min, max] = [max, min];

    emit('apply-price', { min, max });
    close();
  }

  function resetPrice(close: () => void): void {
    pendingMin.value = '';
    pendingMax.value = '';
    emit('apply-price', { min: null, max: null });
    close();
  }

  function handlePriceToggle(open: boolean, toggle: () => void): void {
    if (!open) syncPendingFromApplied();
    toggle();
  }

  //#endregion
</script>

<template>
  <header class="pos-v2-history-header">
    <div class="header-left">
      <PosUiButton
        v-if="showBack"
        color="neutral"
        variant="ghost"
        size="md"
        icon="arrow-left"
        class="back-button"
        :aria-label="$t('posV2.backToPos')"
        @click="emit('back')"
      />

      <div class="history-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="tab"
          :class="{ active: typeTab === tab.value }"
          @click="emit('update:typeTab', tab.value)"
        >
          <PosUiIcon :name="tab.icon" class="tab-icon" />
          <span class="tab-label">{{ tab.label }}</span>
        </button>
      </div>

      <PosV2HistoryDatePopover
        :range="dateRange"
        @apply="(range) => emit('apply-date', range)"
      />

      <PosV2HistoryStatusPopover
        v-if="!isTablesTab"
        :statuses="statuses"
        @apply="(value) => emit('apply-statuses', value)"
      />

      <PosV2HistoryTablesPopover
        v-if="isTablesTab && tableOptions.length > 0"
        :options="tableOptions"
        :table-ids="tableIds"
        @apply="(value) => emit('apply-tables', value)"
      />

      <PosUiPopover v-if="!isTablesTab" placement="bottom" align="start">
        <template #trigger="{ open, toggle }">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="md"
            icon="currency-usd"
            class="price-trigger"
            @click="handlePriceToggle(open, toggle)"
          >
            <span class="price-trigger-label">
              {{ $t('posV2.historyPrice') }}
            </span>
            <span v-if="priceNarrowed" class="price-active-dot" />
          </PosUiButton>
        </template>

        <template #default="{ close }">
          <div class="price-panel">
            <div class="price-inputs">
              <div class="price-field">
                <label class="price-label">
                  {{ $t('posV2.historyPriceFrom') }}
                </label>
                <PosUiInput
                  :value="pendingMin"
                  type="text"
                  inputmode="decimal"
                  size="sm"
                  :prefix="currencySymbol"
                  placeholder="0"
                  @input="(v) => (pendingMin = v)"
                  @enter="applyPrice(close)"
                />
              </div>
              <div class="price-field">
                <label class="price-label">
                  {{ $t('posV2.historyPriceTo') }}
                </label>
                <PosUiInput
                  :value="pendingMax"
                  type="text"
                  inputmode="decimal"
                  size="sm"
                  :prefix="currencySymbol"
                  :placeholder="i18nT('posV2.historyPriceNoLimit')"
                  @input="(v) => (pendingMax = v)"
                  @enter="applyPrice(close)"
                />
              </div>
            </div>
            <div class="price-actions">
              <PosUiButton
                color="neutral"
                variant="ghost"
                size="sm"
                @click="resetPrice(close)"
              >
                {{ $t('posV2.historyPriceReset') }}
              </PosUiButton>
              <PosUiButton
                color="primary"
                variant="solid"
                size="sm"
                @click="applyPrice(close)"
              >
                {{ $t('posV2.historyPriceApply') }}
              </PosUiButton>
            </div>
          </div>
        </template>
      </PosUiPopover>
    </div>

    <div class="header-search">
      <PosUiInput
        :value="search"
        icon="magnify"
        size="md"
        clearable
        :placeholder="searchPlaceholder"
        @input="(v) => emit('update:search', v)"
      />
    </div>

    <div class="header-right">
      <div class="results-badge">
        <PosUiIcon name="history" class="results-icon" />
        <span class="results-count">{{ total }}</span>
        <span class="results-label">{{ $t(resultsLabelKey) }}</span>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .pos-v2-history-header {
    @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

    .header-left {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-gap-2;

      .back-button {
        @apply tw-flex-none;
      }

      // Segmented control matching PosV2ModeTabs.
      .history-tabs {
        @apply tw-flex tw-gap-0.5 tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

        .tab {
          @apply tw-flex tw-min-h-[40px] tw-cursor-pointer tw-select-none tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-border tw-border-transparent tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

          &:hover {
            @apply tw-text-neutral-800;
          }

          &.active {
            @apply tw-border-neutral-200 tw-bg-white tw-font-semibold tw-text-neutral-900 tw-shadow-sm;
          }

          .tab-icon {
            @apply tw-flex-none tw-text-base;
          }

          .tab-label {
            @apply tw-hidden sm:tw-inline;
          }
        }
      }

      .price-trigger {
        @apply tw-relative;

        .price-trigger-label {
          @apply tw-hidden md:tw-inline;
        }

        .price-active-dot {
          @apply tw-absolute tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-orange-600;
        }
      }
    }

    .price-panel {
      @apply tw-flex tw-flex-col tw-gap-3;

      .price-inputs {
        @apply tw-flex tw-gap-2;

        .price-field {
          @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1;

          .price-label {
            @apply tw-text-xs tw-font-medium tw-text-neutral-500;
          }
        }
      }

      // Sticky within the scrolling popover panel (max-height can be under
      // the content height on short viewports): Aplicar must always be
      // visible. Negative offset because sticky respects the scroller's p-3.
      .price-actions {
        @apply tw-sticky tw--bottom-3 tw-z-10 tw--mx-3 tw--mb-3 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-100 tw-bg-white tw-px-3 tw-py-2 tw-shadow-[0_-4px_6px_-4px_rgba(15,23,42,0.12)];
      }
    }

    .header-search {
      @apply tw-order-3 tw-w-full tw-min-w-[150px] tw-max-w-md tw-flex-1 sm:tw-order-none sm:tw-w-auto;
    }

    .header-right {
      @apply tw-ml-auto tw-flex tw-flex-none tw-items-center;

      .results-badge {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-neutral-100 tw-px-3 tw-py-2 tw-text-neutral-600;

        .results-icon {
          @apply tw-text-base tw-text-neutral-400;
        }

        .results-count {
          @apply tw-font-bold tw-tabular-nums tw-text-neutral-900;
        }

        .results-label {
          @apply tw-hidden tw-text-sm sm:tw-inline;
        }
      }
    }
  }
</style>
